/**
 * Trunnion AI - Global Search Query Hook
 *
 * React Query hook with 300ms debounce for the global search endpoint.
 * Only fires when the query string is at least 2 characters long.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/config/queryKeys';
import { searchApi } from '@/api/search.api';
import type { SearchEntityType } from '@/types';

/**
 * Internal hook to debounce a string value.
 *
 * @param value - The value to debounce.
 * @param delay - Debounce delay in milliseconds.
 * @returns       The debounced value.
 */
function useDebouncedValue(value: string, delay: number): string {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Perform a debounced global search across platform entities.
 *
 * @param query - Raw search query string (debounced internally at 300ms).
 * @param type  - Optional entity type filter (e.g. "workflow", "agent").
 *                When provided, only that type is searched.
 * @returns       Standard React Query result with `SearchResult[]` data.
 */
export function useGlobalSearch(query: string, type?: SearchEntityType) {
  const debouncedQuery = useDebouncedValue(query, 300);

  // Map singular entity type to the plural form used by the API
  const typesParam = type
    ? `${type}s` === 'audits'
      ? 'audit'
      : `${type}s`
    : undefined;

  return useQuery({
    queryKey: queryKeys.search.list({ q: debouncedQuery, type }),
    queryFn: () => searchApi.globalSearch(debouncedQuery, typesParam),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  });
}
